import { EditOutlined } from "@ant-design/icons"
import { Button, Flex, Typography } from "antd"
import { type BaseOptionType } from "antd/lib/select"
import React, { useState } from "react"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"

import { useDateFormat } from "../../../../../hooks/use-date-format.ts"
import { cn } from "../../../../../utils/cn.ts"
import { calendarDeepLink } from "../../../../Calendar/helpers/calendar-deep-link.ts"
import { formatFollowUp } from "../../../../Leads/helpers/lead-table-format.ts"
import { LEAD_STATUSES } from "../../../constants/lead-statuses.ts"
import { type Lead } from "../../../types/lead.types.ts"
import {
  LeadScheduleEditorModal,
  type LeadScheduleKind
} from "../../schedule/LeadScheduleEditorModal.tsx"
import { type LeadHeaderPatch } from "./lead-header-patch.ts"

const { Text } = Typography

interface ScheduleReadingProps {
  label: string
  value: string
  empty: string
  editLabel: string
  canEdit?: boolean
  onEdit: () => void
  testId: string
  calendarHref?: string | null
  /** Named by a rejected stage transition; the label carries the mark. */
  missing?: boolean
}

function ScheduleReading({
  label,
  value,
  empty,
  editLabel,
  canEdit,
  onEdit,
  testId,
  calendarHref,
  missing
}: ScheduleReadingProps) {
  return (
    <Flex
      vertical
      gap={2}
      data-testid={testId}
      data-missing={missing ? "true" : undefined}
      className={cn(
        missing && "border border-error bg-error-bg rounded-md px-2 py-1"
      )}
    >
      <Text type={missing ? "danger" : "secondary"} className={"text-xs"}>
        {label}
      </Text>
      <Flex gap={4} align={"center"}>
        {/* An unset optional field is muted, never an error reading. */}
        {value ? (
          calendarHref ? (
            <Link to={calendarHref}>{value}</Link>
          ) : (
            <Text>{value}</Text>
          )
        ) : (
          <Text type={"secondary"}>{empty}</Text>
        )}
        {canEdit && (
          <Button
            size={"small"}
            type={"text"}
            aria-label={editLabel}
            icon={<EditOutlined />}
            onClick={onEdit}
          />
        )}
      </Flex>
    </Flex>
  )
}

interface LeadWorkspaceScheduleProps {
  lead: Lead
  owners: BaseOptionType[]
  canEdit?: boolean
  saving?: boolean
  /** Named by a rejected stage transition; the appointment control carries the mark. */
  appointmentMissing?: boolean
  onChange: (patch: LeadHeaderPatch) => Promise<boolean>
}

export function LeadWorkspaceSchedule({
  lead,
  owners,
  canEdit,
  saving,
  appointmentMissing,
  onChange
}: LeadWorkspaceScheduleProps) {
  const { t } = useTranslation()
  const { userFormat, normalizeDate } = useDateFormat()
  const [editing, setEditing] = useState<LeadScheduleKind | null>(null)

  const isRehash = lead?.status === LEAD_STATUSES.NURTURING

  const followUp = formatFollowUp(lead?.follow_up_date, lead?.follow_up_time, {
    isRehash,
    instant: lead?.follow_up_at,
    userFormat
  })
  const appointment = formatFollowUp(
    lead?.appointment_date,
    lead?.appointment_time,
    { instant: lead?.appointment_at, userFormat }
  )

  return (
    <>
      <ScheduleReading
        testId={"lead-header-follow-up"}
        label={t("leadWorkspace.header.followUp.label")}
        value={followUp}
        empty={t("leadWorkspace.header.followUp.none")}
        editLabel={t("leadWorkspace.header.followUp.edit")}
        canEdit={canEdit}
        calendarHref={
          lead?.follow_up_date
            ? calendarDeepLink(
                normalizeDate(lead.follow_up_date),
                lead?.follow_up_event
              )
            : null
        }
        onEdit={() => setEditing("followUp")}
      />

      <div className={"hidden w-px self-stretch bg-gray-200 sm:block"} />

      <ScheduleReading
        testId={"lead-header-appointment"}
        label={t("leadWorkspace.header.appointment.label")}
        value={appointment}
        empty={t("leadWorkspace.header.appointment.none")}
        editLabel={t("leadWorkspace.header.appointment.edit")}
        canEdit={canEdit}
        missing={appointmentMissing}
        calendarHref={
          lead?.appointment_date
            ? calendarDeepLink(
                normalizeDate(lead.appointment_date),
                lead?.appointment_event
              )
            : null
        }
        onEdit={() => setEditing("appointment")}
      />

      {canEdit && (
        <LeadScheduleEditorModal
          kind={editing}
          lead={lead}
          owners={owners}
          saving={saving}
          onChange={onChange}
          onClose={() => setEditing(null)}
        />
      )}
    </>
  )
}
